import _ from 'underscore';
import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Wizard,
  LayoutContainer,
  Layout,
  StepContainer,
} from '@bingads-webui-react/wizard';
import {
  SaveBlocker,
  DefaultTemplate,
  CancelWizardDialog,
} from '@bingads-webui-react/wizard-ui-library';
import { FluentSwitcherContext } from '@bingads-webui-react/fluent-version-switcher';
import { withTheme } from 'react-jss';
import { WizardPerfMarker } from '@bingads-webui-campaign/wizard-performance-marker';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { withScope } from '@bingads-webui-react/scope-service';
import { withRecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { getNeedConfirming } from '@bingads-webui-campaign-react/campaign-settings-utils';

import { STEP_PATHS } from './steps/step-constants';
import { useAssetGroupSettingStepRepository } from './steps/asset-group-setting';
import { AssetsStepShell } from './steps/assets';
import { PerfMaxSettingStepFooter } from './steps';
import { WizardLogger } from './shared/components/wizard-logger';
import { PMAX_WIZARD_ID_NAME } from './constants';
import { ProductsStepShell } from './steps/products';

const StepConfig = [
  {
    id: STEP_PATHS.ASSETGROUPSETTING,
    view: props => (
      <Activity name={STEP_PATHS.ASSETGROUPSETTING}>
        <AssetsStepShell {...props} />
        {props?.data?.campaign?.ProviderId && <ProductsStepShell {...props} isSettingPage />}
      </Activity>),
    repository: useAssetGroupSettingStepRepository,
  },
];

export const getAssetStepConfig = permissions => StepConfig.map((step) => {
  if (_.get(permissions, ['dynamic', 'ConversionAssociationWarningP2']) && step.id === STEP_PATHS.ASSETGROUPSETTING) {
    const mapStep = {
      ...step,
      machineOptions: {
        ...step.machineOptions,
        guards: {
          needsConfirming: context => getNeedConfirming(permissions, context),
        },
      },
    };
    return mapStep;
  }
  return step;
});

const PerfMaxNorthStarAssetGroupSettingWizardInternal = ({
  perfMarker,
  scope,
  permissions,
}) => {
  const instrumentation = useContext(InstrumentationContext);

  const wizardPerfMarker = useMemo(() => (
    perfMarker.createChild('PerfMaxNorthStarWizardAssetGroupSetting')
  ), [perfMarker]);

  const routeToNavigateToWhenDone = '/campaign/vnext/assetgroups';
  const wizardName = 'PerfMaxNorthStarWizard-asset-group-setting';

  const { dynamic: { AppsAdsPerfMaxFluentV2 } } = permissions;

  const wizardConfig = useMemo(() => ({
    id: PMAX_WIZARD_ID_NAME.PMAX_NORTH_STAR_SETTING_WIZARD,
    initialStep: STEP_PATHS.ASSETGROUPSETTING,
    steps: getAssetStepConfig(permissions),
    initialFlowFactors: { flow: STEP_PATHS.ASSETGROUPSETTING },
    initialWizardData: {
      flow: STEP_PATHS.ASSETGROUPSETTING,
    },
    getFlow: [STEP_PATHS.ASSETGROUPSETTING],
    actions: {
      onDone: () => scope.save(routeToNavigateToWhenDone),
      onExited: () => scope.save(routeToNavigateToWhenDone),
    },
    attachStepActivities: false,
  }), [permissions, scope]);

  return (
    <FluentSwitcherContext.Provider value={AppsAdsPerfMaxFluentV2}>
      <PerformanceMarkerProvider
        name={wizardName}
        instrumentation={instrumentation}
        immediateDone={false}
      >
        <Scenario name={wizardName}>
          <Activity name="DefaultWizardActivity">
            <Wizard config={wizardConfig}>
              <WizardPerfMarker wizardName={wizardName} wizardMarker={wizardPerfMarker} >
                <LayoutContainer template={DefaultTemplate}>
                  <Layout type="StepContainer" component={StepContainer} />
                  <Layout type="Blocker" component={SaveBlocker} />
                  <Layout type="CancelDialog" component={CancelWizardDialog} />
                  <Layout
                    type="Footer"
                    component={PerfMaxSettingStepFooter}
                    cancelButtonText={_TL_('Cancel')}
                  />
                </LayoutContainer>
                <WizardLogger />
              </WizardPerfMarker>
            </Wizard>
          </Activity>
        </Scenario>
      </PerformanceMarkerProvider>
    </FluentSwitcherContext.Provider>
  );
};

PerfMaxNorthStarAssetGroupSettingWizardInternal.propTypes = {
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    save: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AppsAdsPerfMaxFluentV2: PropTypes.bool,
      ConversionAssociationWarningP2: PropTypes.bool,
    }),
  }).isRequired,
  siteMap: PropTypes.shape({}).isRequired,
  dynamicOptions: PropTypes.shape({}).isRequired,
};

export const PerfMaxNorthStarAssetGroupSettingWizard = withSiteMap(withPermissions(withRecommendationContext(withScope(withTheme(PerfMaxNorthStarAssetGroupSettingWizardInternal, undefined, undefined, false, true)))));
